import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Lock, MessageCircle, FileDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/AuthContext";
import { PlanStatusChip } from "@/components/plan/PlanWidgets";
import { fmtDate, fmtDateOd, fmtDateTime, rupees, sourceText, useMyPlan } from "@/lib/planDisplay";
import { inWordsEN } from "@/lib/numberToWords";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER;
const SOURCE_SHORT = { payment: "Paid online", admin: "By admin", partner: "Teacher Utility ToolKit", trial: "Free trial", auto_setup: "After setup" };

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 border-b border-border/60 py-2 text-sm last:border-0">
    <span className="text-ink-700">{label}</span><span className="text-right font-semibold text-ink-900" data-testid={testId}>{value}</span>
  </div>
);

const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

// Receipt rendered as HTML inside .report-scope → html2canvas → A4 PDF (Odia glyphs stay correct).
async function downloadReceipt(historyId) {
  const r = (await axios.get(`${API}/subscriptions/receipt/${historyId}`)).data;
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const b = r.business;
  const host = document.createElement("div");
  host.className = "report-scope";
  host.style.cssText = "position:fixed;left:-10000px;top:0;width:794px;background:#fff;color:#111;font-family:'Noto Sans Oriya','Noto Sans',sans-serif;padding:48px;";
  const line = (k, v) => `<tr><td style="padding:6px 0;color:#555;width:42%">${k}</td><td style="padding:6px 0;font-weight:600">${esc(v)}</td></tr>`;
  host.innerHTML = `
    <div style="border-bottom:2px solid #222;padding-bottom:12px">
      <div style="font-size:24px;font-weight:700">${esc(b.name)}</div>
      <div style="font-size:13px;color:#555">Product: ${esc(b.product)}</div>
      <div style="font-size:12px;color:#555;margin-top:4px">${esc(b.name)} · ${esc(b.location)} · WhatsApp ${esc(b.whatsapp)} · ${b.emails.map(esc).join(" · ")}</div>
    </div>
    <div style="font-size:20px;font-weight:700;margin:20px 0 4px">Payment Receipt · ଦେୟ ରସିଦ</div>
    <table style="width:100%;font-size:14px;border-collapse:collapse;margin-top:8px">
      ${line("Receipt No. · ରସିଦ ନଂ", r.receiptNo)}
      ${line("Date &amp; time (IST) · ତାରିଖ", fmtDateTime(r.paidAt))}
      ${line("School · ବିଦ୍ୟାଳୟ", r.schoolName)}
      ${line("UDISE", r.udiseCode)}
      ${line("Plan · ପ୍ଲାନ", r.planLabel)}
      ${r.validityDays ? line("Validity · ମିଆଦ", `${r.validityDays} days${r.activeUntil ? ` (till ${fmtDate(r.activeUntil)})` : ""}`) : ""}
      ${line("Amount paid · ଦେୟ ରାଶି", `₹${Number(r.amountRupees).toFixed(2)}`)}
      ${line("Amount in words · ଶବ୍ଦରେ", inWordsEN(r.amountRupees))}
      ${line("Payment mode · ଦେୟ ପ୍ରକାର", r.paymentMode)}
      ${line("Payment ID", r.paymentIdMasked)}
    </table>
    <div style="margin-top:48px;font-size:12px;color:#666;border-top:1px solid #ccc;padding-top:10px">This is a computer-generated receipt. · ଏହା କମ୍ପ୍ୟୁଟର ଦ୍ୱାରା ପ୍ରସ୍ତୁତ ରସିଦ।</div>`;
  document.body.appendChild(host);
  try {
    const canvas = await html2canvas(host, { scale: 2, backgroundColor: "#ffffff" });
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const w = pdf.internal.pageSize.getWidth();
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, w, (canvas.height * w) / canvas.width);
    pdf.save(`Receipt-${r.receiptNo.replace(/\//g, "-")}.pdf`);
  } finally { host.remove(); }
}

const HistoryCard = ({ h }) => {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    setBusy(true);
    try { await downloadReceipt(h.id); } catch { toast.error("Could not download the receipt"); } finally { setBusy(false); }
  };
  return (
    <li className="rounded-xl border border-border bg-white p-3" data-testid={`plan-history-${h.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-ink-900">{h.event === "activation" ? h.planLabel : `${h.eventLabel}${h.planLabel ? ` · ${h.planLabel}` : ""}`}</span>
        <span className="text-xs text-ink-700">{fmtDate(h.createdAt)}</span>
      </div>
      <p className="mt-1 text-xs text-ink-700">
        {h.amountRupees > 0 && <>{rupees(h.amountRupees)} · </>}
        {h.validityDays ? `${h.validityDays} days · ` : ""}{SOURCE_SHORT[h.source] || ""}
        {h.paymentIdMasked && <> · <span className="font-mono">{h.paymentIdMasked}</span></>}
      </p>
      {h.hasReceipt && (
        <button type="button" onClick={get} disabled={busy} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-moss-dark hover:underline" data-testid={`plan-receipt-${h.id}`}>
          <FileDown className="h-3.5 w-3.5" /> {busy ? "Preparing…" : "Download receipt (PDF) · ରସିଦ"}
        </button>
      )}
    </li>
  );
};

export default function MyPlan() {
  const { user } = useAuth();
  const plan = useMyPlan(user?.role === "school" ? user.id : null);
  const src = sourceText(plan?.source, plan?.startedAt);
  return (
    <main className="min-h-screen bg-sand-50 pb-24 text-ink-900">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-6">
        <h1 className="font-heading text-3xl font-bold" data-testid="my-plan-title">My Plan · ମୋ ପ୍ଲାନ</h1>
        {user?.role !== "school" ? <p className="mt-6 text-sm text-ink-700" data-testid="my-plan-admin-note">My Plan is for school accounts. Use “Plan history” on a school in the admin users list.</p>
          : !plan ? <p className="mt-6 text-sm text-ink-700" data-testid="my-plan-loading">Loading…</p> : (
          <>
            <section className="mt-5 rounded-2xl border border-border bg-white p-5" data-testid="my-plan-current">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-lg font-bold" data-testid="my-plan-name">{plan.planLabel || "No active plan · କୌଣସି ପ୍ଲାନ ନାହିଁ"}</h2>
                <PlanStatusChip plan={plan} testId="my-plan-status" />
              </div>
              <div className="mt-3">
                {plan.amountRupees > 0 && <Row label="Price paid · ଦେୟ" value={rupees(plan.amountRupees)} testId="my-plan-price" />}
                <Row label="Started on · ଆରମ୍ଭ" value={plan.startDateRecorded ? `${fmtDate(plan.startedAt)} · ${fmtDateOd(plan.startedAt)}` : "Start date not recorded"} testId="my-plan-started" />
                {plan.activeUntil && <Row label="Valid till · ମିଆଦ" value={`${fmtDate(plan.activeUntil)} · ${fmtDateOd(plan.activeUntil)}`} testId="my-plan-valid" />}
                {plan.daysLeft > 0 && <Row label="Days left · ବାକି ଦିନ" value={plan.daysLeft} testId="my-plan-days" />}
                {plan.planLabel && <Row label="How it was activated · କିପରି ସକ୍ରିୟ" value={`${src.en} · ${src.od}`} testId="my-plan-source" />}
              </div>
              <Link to="/choose-plan" className="mt-4 inline-block rounded-full bg-clay px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-clay-dark" data-testid="my-plan-renew">Renew / Upgrade · ନବୀକରଣ</Link>
            </section>

            <section className="mt-5 rounded-2xl border border-border bg-white p-5" data-testid="my-plan-features">
              <h2 className="font-heading text-base font-bold">What’s included · କ’ଣ ସାମିଲ</h2>
              <ul className="mt-3 space-y-2">
                {plan.featureList.map((f) => (
                  <li key={f.key} className={`flex items-center gap-2 text-sm ${f.included ? "text-ink-900" : "text-ink-700/60"}`} data-testid={`my-plan-feature-${f.key}`} data-included={f.included}>
                    {f.included ? <Check className="h-4 w-4 text-teal-700" /> : <Lock className="h-4 w-4" />}
                    <span>{f.name}{f.nameOd ? ` · ${f.nameOd}` : ""}</span>
                    {!f.included && <Link to="/choose-plan" className="ml-auto text-xs font-semibold text-clay-dark hover:underline">Upgrade to unlock</Link>}
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-5" data-testid="my-plan-history">
              <h2 className="font-heading text-base font-bold">History · ଇତିହାସ</h2>
              {plan.history.length === 0 ? <p className="mt-2 text-sm text-ink-700" data-testid="my-plan-history-empty">No plan history recorded yet.</p> : (
                <ul className="mt-3 space-y-2">{plan.history.map((h) => <HistoryCard key={h.id} h={h} />)}</ul>
              )}
            </section>

            <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hello, I need help with my plan.")}`} target="_blank" rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-ink-900 hover:bg-sand-100" data-testid="my-plan-help">
              <MessageCircle className="h-4 w-4 text-teal-700" /> Problem with your plan? Chat on WhatsApp · ସାହାଯ୍ୟ
            </a>
          </>
        )}
      </div>
    </main>
  );
}
